import { For, ParentProps, Show, Signal, createContext, createEffect, createSignal, onMount } from 'solid-js'
import { requireContext, toast } from 'common'
import { HistoryItem, QueryAssistContext } from './QueryAssistContext'
import { queryAssist as api, PreviewBO, PreviewProperty, QueryPreview } from 'st-app/api'
import styles from './st-query-assist.module.css'
import Textarea from '../st-textarea/st-textarea';
import Button from '../st-button/st-button';
import { showDialog } from '../st-modal/st-modal'
import Accordion from '../st-accordion/st-accordion'
import STAILoader from '../st-ai-loader/st-ai-loader'

const QueryAssistContextJsx = createContext<QueryAssistContext>();
export const requireQueryAssistContext = () => {
    return requireContext(QueryAssistContextJsx);
}

export class QueryAssistProps {
    preview?: Signal<QueryPreview>
}

export default function QueryAssist(props: ParentProps<QueryAssistProps>) {
    const [preview, setPreview] = props.preview ?? createSignal(new QueryPreview());
    const context = new QueryAssistContext(preview());

    createEffect(() => {
        setPreview(context.preview);
    })

    return (
        <QueryAssistContextJsx.Provider value={context}>
            <QueryAssistInternal {...props} />
        </QueryAssistContextJsx.Provider>
    )
}

function QueryAssistInternal(props: ParentProps<QueryAssistProps>) {
    const queryAssist = requireQueryAssistContext();

    const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') {
            queryAssist.submitPrompt();
        }
    }

    const copyToClipboard = () => {
        navigator.clipboard.writeText(queryAssist.preview.stQueryString ?? '');
        toast.notify('success', 'Query string copied to clipboard');
    }

    createEffect(() => {
        const startingJson = JSON.stringify(queryAssist.preview);

        api.GetQueryString(queryAssist.preview).then(q => {
            // If the preview has changed since the query was sent, don't update the preview
            if (JSON.stringify(queryAssist.preview) !== startingJson) return;
            queryAssist.setPreview(p => ({ ...p, stQueryString: q }));
        })
    })

    const submitRating = async (item: HistoryItem, isHelpful: boolean) => {
        try {
            if (item.preview.aiLogGU) {
                await api.SetUserRating(item.preview.aiLogGU, isHelpful);
                toast.notify('success', 'Rating submitted');
            }
        } catch(e) {
            console.error(e);
            toast.notify('error', 'Error submitting rating');
        }
    }

    if (import.meta.env.DEV) {
        onMount(() => {
            // queryAssist.setPrompt('Write me an ST Query that returns a list of students with their first name, last name, and sis number as well as the program code tied to each of their enrollment activities. I also want to see the school name/year. Your response will be fed directly into the "Type in Query" textbox, so only reply with the query string itself.')
            queryAssist.setPrompt('I want a list of students with their first name, last name, and sis number')
            // submitPrompt();
        })
    }

    return (
        <div class={styles['st-query-assist']}>
            <div class={styles['chat-panel']}>
                <Textarea rows={8} value={[queryAssist.prompt, queryAssist.setPrompt]} label='' onKeyDown={onKeyDown}/>

                <div style='display: grid; grid-template-columns: 1fr auto;'>
                    <Button onClick={() => queryAssist.submitPrompt()} icon='fa fa-brain' loading={queryAssist.loadingPreview()} enabled={!!queryAssist.prompt()}>
                        Submit
                    </Button>
                    {/* <Show when={queryAssist.preview?.stQueryString}> */}
                        <div style='margin-left: 0.5em;'>
                            <Button enabled={!!queryAssist.preview?.stQueryString} type='warning' onClick={() => queryAssist.reset()} icon='fa fa-undo'>Reset</Button>
                        </div>
                    {/* </Show> */}
                </div>


                <div class={styles['history-panel']}>
                    <For each={queryAssist.history().toReversed()}>{item => (
                        <div class={styles['history-entry']} data-selected={queryAssist.selectedHistoryId() === item.id} onClick={() => queryAssist.selectHistoryItem(item)}>
                            <i class='fa fa-history'></i>
                            <span use:tooltip={item.prompt}>{item.prompt}</span>

                            <div class={styles['history-entry-actions']}>
                                <button class={styles['rating-button']} data-selected={item.isHelpful === false} onClick={() => queryAssist.setUserRating(item, false)}>
                                    <i class='fa fa-thumbs-down'></i>
                                </button>
                                <button class={styles['rating-button']} data-selected={item.isHelpful === true} onClick={() => queryAssist.setUserRating(item, true)}>
                                    <i class='fa fa-thumbs-up'></i>
                                </button>
                            </div>
                        </div>
                    )}</For>
                </div>

            </div>
            <div class={styles['preview-panel']}>
                <Show when={!queryAssist.loadingPreview()} fallback={<Loading />}>
                    <Show when={queryAssist.preview?.primaryBO?.ID}>
                        <BONode bo={queryAssist.preview!.primaryBO} />
                    </Show>
                </Show>
            </div>

            <div class={styles['query-preview']}>
                <Accordion>
                    <Accordion.Item title='Full Query String' titlePlacement='left' disabled={!queryAssist.preview.stQueryString}>
                        <div class={styles['query-string-container']}>
                            <span>{queryAssist.preview.stQueryString}</span>
                            <i class='fa fa-copy' style='cursor: pointer;' onClick={copyToClipboard}></i>
                        </div>
                    </Accordion.Item>
                    <Show when={queryAssist.preview.rawResponse}>
                        <Accordion.Item title='Raw LLM Output' titlePlacement='left'>
                            <div class={styles['query-string-container']}>
                                <span>{queryAssist.preview.rawResponse}</span>
                                <i class='fa fa-copy' style='cursor: pointer;' onClick={copyToClipboard}></i>
                            </div>
                        </Accordion.Item>
                    </Show>
                </Accordion>
            </div>
        </div>
    );
}
QueryAssist.defaultProps = new QueryAssistProps();

function Loading() {
    return (
        <div style='width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;'>
            <STAILoader processing={true} size='350px' />
        </div>
    )
}

function BONode(props: { bo: PreviewBO }) {
    const queryAssist = requireQueryAssistContext();

    const removeSelf = () => {
        queryAssist.removeBO(props.bo);
    }

    return (
        <div class={styles['bo-node-container']}>
            <div class={styles['bo-node']}>
                <div class={styles['bo-node-name']}>
                    {props.bo.name}
                </div>
                
                <Show when={props.bo.BOID !== queryAssist.preview.primaryBO.BOID}>
                    <button onClick={removeSelf} class={styles['remove-button']}>
                        <i class='fa fa-trash'></i>
                    </button>
                </Show>
            </div>
            
            <div class={styles['bo-node-properties']}>
                <For each={props.bo.properties}>{property => (
                    <PropertyNode bo={props.bo} property={property} />
                )}</For>
            </div>
            
            <For each={props.bo.childBOs}>{child => (
                <BONode bo={child} />
            )}</For>

        </div>
    )
}

function PropertyNode(props: { bo: PreviewBO, property: PreviewProperty }) {
    const queryAssist = requireQueryAssistContext();

    const removeSelf = async () => {
        queryAssist.removeProperty(props.bo, props.property);
    }

    return (
        <div class={styles['property-node']}>
            <div class={styles['property-node-name']}>
                {props.property.srcElement}
            </div>

            <button onClick={removeSelf} class={styles['remove-button']}>
                <i class='fa fa-trash'></i>
            </button>
        </div>
    )
}


export const launchQueryAssist = async (queryXml?: string) => {
    return showDialog<string>((resolve, reject) => {
        const [preview, setPreview] = createSignal(new QueryPreview());
        const [loading, setLoading] = createSignal(false);
        const [loadingInitial, setLoadingInitial] = createSignal(true);

        const submit = async () => {
            setLoading(true);
            try {
                const p = preview();
                const query = await api.GetQueryString(p);
                resolve(query);
            } catch(e) {
                console.error(e);
                toast.notify('error', 'An unexpected error occurred while generating the query string');
            } finally {
                setLoading(false);
            }
        }

        onMount(async () => {
            try {
                if (queryXml) {
                    const p = await api.LoadFromQueryXML(queryXml);
                    setPreview(p);
                }
            } catch(e) {
                console.error(e);
                toast.notify('error', 'An unexpected error occurred while loading the query');
            } finally {
                setLoadingInitial(false);
            }
        })

        return (
            <Show when={!loadingInitial()} fallback={<Loading />}>
                <div style='display: grid; gap:1em;'>
                    <QueryAssist preview={[preview, setPreview]} />
                    <Button type='primary' onClick={submit} loading={loading()} enabled={!!preview()}>Use This Query</Button>
                </div>
            </Show>
        )
    }, { title: 'Query Assist' })
}